// Phone top bar: back to Home (the sessions), what's on screen, command palette, new session.

import { IconChevronLeft, IconPlus, IconSearch } from '@tabler/icons-react';
import { useStore } from '../store';
import { peekTerm } from '../term/manager';
import { Ic, Mark } from './ds';
import { openSheet } from './sheets';
import { statusMark } from './PaneBar';
import { stateText, stateWords, useNow } from './time';
import { focusedSession } from './tabs';
import { useActions } from './Workspace';

/** Tabler's "select text" glyph (lines and a caret), drawn as the canvas has it: the installed @tabler/icons-react has no icon for it. */
const SelectTextIcon = () => (
  <svg className="ds-ic" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 6h16" />
    <path d="M4 12h9" />
    <path d="M4 18h6" />
    <path d="M17 13v7" />
    <path d="M15 13h4" />
    <path d="M15 20h4" />
  </svg>
);


export function MobileTopBar({ atHome }: { atHome: boolean }) {
  const a = useActions();
  const now = useNow();
  const sessions = useStore((s) => s.sessions);
  const desktopRunning = useStore((s) => s.desktopRunning);
  const desktopBusy = useStore((s) => s.desktopBusy);
  const sid = focusedSession(a.tabs);
  const session = sid !== null ? sessions.find((s) => s.id === sid) : undefined;
  // Another session wants attention: flag it on the back button.
  const attention = sessions.some((s) => s.status === 'waiting' && s.id !== session?.id);

  let title = 'cmx';
  let status = sessions.length ? `${sessions.length} session${sessions.length === 1 ? '' : 's'}` : 'No sessions';
  let mark: Parameters<typeof Mark>[0] = { tone: 'idle' };
  if (session) {
    title = session.name || 'Shell';
    // the line under the name: the sidebar row's words (spec "Session rows"), counting on the machine's clock
    status = stateText(stateWords(session), now);
    mark = statusMark(session.status);
  } else if (a.tabs.page === 'desktop') {
    title = 'Desktop';
    status = desktopBusy ? (desktopRunning ? 'Stopping' : 'Starting') : desktopRunning ? 'Running' : 'Stopped';
    mark = { tone: desktopRunning ? 'live' : desktopBusy ? 'attn' : 'idle' };
  }

  return (
    <header className="mbar" data-testid="mobile-topbar" inert={atHome}>
      <button
        className="mbar-btn"
        onClick={a.goHome}
        aria-label="Sessions"
        data-testid="home-back"
      >
        <Ic icon={IconChevronLeft} size={20} />
        {attention && <span className="mbar-flag" aria-label="A session needs input" />}
      </button>
      <button className="mbar-title" onClick={a.openPalette} aria-label={`${title}, ${status}. Switch session`} data-testid="mbar-title">
        <Mark {...mark} />
        <span className="mbar-text">
          <span className="mbar-name" data-testid="mbar-name">
            {title}
          </span>
          <span className="mbar-status">{status}</span>
        </span>
      </button>
      {session && (
        <button
          className="mbar-btn"
          onClick={() => {
            const t = peekTerm(session.id);
            // Recent output as plain text (soft-wrapped lines joined, so long links stay whole).
            if (t) openSheet({ kind: 'select', sid: session.id, text: t.text().split('\n').slice(-2000).join('\n') });
          }}
          aria-label="Select text"
          data-testid="mbar-select"
        >
          <SelectTextIcon />
        </button>
      )}
      <button className="mbar-btn" onClick={a.openPalette} aria-label="Command palette" data-testid="mbar-palette">
        <Ic icon={IconSearch} size={20} />
      </button>
      <button className="mbar-btn" onClick={() => void a.newSession()} aria-label="New session" data-testid="mbar-new-session">
        <Ic icon={IconPlus} size={20} />
      </button>
    </header>
  );
}
